import React, { useState, useCallback, useMemo } from 'react'
import injectSheet from 'react-jss'
import FormLabel from 'pobble-components/dist/form-elements/form-label'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import Button from 'pobble-components/dist/button'
import {
  apiSetPresentationAsRecommended,
  apiSetPresentationCategory,
  apiFlagPresentation,
  apiUnflagPresentation
} from '../../../common/api/admin'
import { toast } from 'pobble-components/dist/toast'
import { CATEGORIES } from 'pobble-components/dist/presentation'
import FormSelect from 'pobble-components/dist/form-elements/form-select'
import FormCheckbox from 'pobble-components/dist/form-elements/form-checkbox'
import { errorHandler } from 'pobble-components/dist/error-handler'
import { withRouter } from 'react-router-dom'
import { apiClient } from 'pobble-components/dist/api'
import { openConfirmationDialog } from 'pobble-components/dist/modal/confirmation-dialog'

const toastText =
  'Presentation status has been updated. Changes will be applied in a couple of seconds. Reload the page'

const styles = {
  container: {
    '& > *': {
      marginTop: '30px',
      '&:first-child': {
        marginTop: '0'
      }
    }
  },
  recommendedControlsRow: {
    display: 'flex'
  },
  setAsRecommended: {
    marginLeft: '10px'
  },
  unsetRecommended: {
    marginLeft: '10px'
  },
  optionGroup: {
    display: 'flex',
    alignItems: 'center'
  },
  lastRow: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  checkboxes: {
    display: 'flex',
    '& > *': {
      marginLeft: '16px'
    }
  },
  p365form: {
    display: 'flex',
    gap: '16px',
    '& button': {
      whiteSpace: 'nowrap'
    }
  },
  audio: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }
}

const Controls = injectSheet(styles)(
  ({ classes, onRecommendedChange, presentation, history }) => {
    const {
      recommended,
      category,
      flag,
      id,
      lessonLink,
      approved,
      publicPresentation,
      premium
    } = useMemo(
      () => ({
        recommended: presentation.recommended_until,
        category: presentation.category,
        flag: presentation.flag,
        id: presentation.id,
        lessonLink: `/lesson/${presentation.id}`,
        approved: presentation.approved,
        publicPresentation: presentation.public,
        premium: presentation.premium
      }),
      [presentation]
    )

    const categories = useMemo(() => {
      return Object.keys(CATEGORIES).map(key => ({
        value: key,
        label: CATEGORIES[key]
      }))
    }, [])

    const [recommendedUntil, setRecommendedUntil] = useState(recommended || '')
    const [selectedCategory, setSelectedCategory] = useState(category)
    const [currentlyFlagged, setCurrentlyFlagged] = useState(flag)
    const [currentlyPremium, setCurrentlyPremium] = useState(premium)
    const [currentlyApproved, setCurrentlyApproved] = useState(approved)
    const [currentlyPublic, setCurrentlyPublic] = useState(publicPresentation)

    const changeApprovedStatus = value => {
      if (value) {
        apiClient
          .put(`/v3/admin/presentations/${id}/approve`)
          .catch(errorHandler)
        setCurrentlyApproved(true)
      } else {
        apiClient
          .put(`/v3/admin/presentations/${id}/decline`)
          .catch(errorHandler)
        setCurrentlyApproved(false)
      }
    }

    const changePublicStatus = value => {
      if (value) {
        apiClient
          .put(`/v3/presentations/${id}/set_to_public`)
          .catch(errorHandler)
        setCurrentlyPublic(true)
      } else {
        apiClient
          .put(`/v3/presentations/${id}/set_to_private`)
          .catch(errorHandler)
        setCurrentlyPublic(false)
      }
    }

    const submitRecommended = useCallback(
      until => {
        const request = apiSetPresentationAsRecommended(id, {
          recommended_until: until
        }).then(() => toast.success(toastText))
        onRecommendedChange &&
          onRecommendedChange({
            id: id,
            date: until,
            request
          })
      },
      [id] // eslint-disable-line
    )
    const changeCategory = useCallback(
      category => {
        setSelectedCategory(category)
        apiSetPresentationCategory(id, { category: category || null })
          .then(() => toast.success(toastText))
          .catch(errorHandler)
      },
      [id]
    )

    const flagPresentation = useCallback(
      checked => {
        setCurrentlyFlagged(checked)
        checked &&
          apiFlagPresentation(id, { reason: 'flagging as an admin' })
            .then(() => toast.success(toastText))
            .catch(errorHandler)
        !checked &&
          apiUnflagPresentation(id, { reason: 'flagging as an admin' })
            .then(() => toast.success(toastText))
            .catch(errorHandler)
      },
      [id]
    )

    function setSponsored(value) {
      apiClient.put(`v3/admin/presentations/${id}/set_sponsored`, {
        sponsored: value
      })
    }

    const setPremium = checked => {
      if (checked) {
        setCurrentlyPremium(true)
        apiClient.put(`v3/admin/presentations/${id}/set_premium`, {
          premium: true
        })
      } else {
        setCurrentlyPremium(false)
        apiClient.put(`v3/admin/presentations/${id}/set_premium`, {
          premium: false
        })
      }
    }

    return (
      <div className={classes.container}>
        <div>
          <FormLabel small>
            {!recommended && `Put presentation in "This week's lessons" until:`}
            {!!recommended &&
              `This presentation is in "This week's lessons" until:`}
          </FormLabel>
          <form
            onSubmit={e => {
              e.preventDefault()
              submitRecommended(recommendedUntil)
            }}
            className={classes.recommendedControlsRow}>
            <FormInput
              value={recommendedUntil}
              onChange={e => setRecommendedUntil(e.currentTarget.value)}
              placeholder="YYYY-MM-DD"
            />
            <Button type="Submit" tall className={classes.setAsRecommended}>
              Set
            </Button>
            {!!recommended && (
              <Button
                action={() => {
                  setRecommendedUntil('')
                  submitRecommended('')
                }}
                dangerSecondary
                tall
                className={classes.unsetRecommended}>
                Unset
              </Button>
            )}
          </form>
        </div>
        <div>
          <FormLabel small>Select a category</FormLabel>
          <FormSelect
            value={selectedCategory}
            onChange={e => changeCategory(e.currentTarget.value)}>
            <option value="">Uncategorized</option>
            {categories.map(({ value, label }) => (
              <option value={value}>{label}</option>
            ))}
          </FormSelect>
        </div>
        <div>
          <FormLabel small>Set as Pobble 365 day</FormLabel>
          <form
            onSubmit={e => {
              e.preventDefault()
              const day = e.target.day.value
              const month = e.target.month.value
              openConfirmationDialog({
                bodyText: `This will swap existing Pobble 365 presentation with this one.`,
                confirmText: 'Set as Pobble 365',
                action: () => {
                  apiClient
                    .put(`v3/presentations/${id}/set_to_public`)
                    .then(() => {
                      apiClient
                        .put(`v3/admin/presentations/${id}/set_category`, {
                          category: 'pobble365'
                        })
                        .then(() => {
                          apiClient
                            .put(
                              `v3/admin/presentations/${id}/swap_for_p365_presentation`,
                              {
                                day,
                                month
                              }
                            )
                            .then(res => {
                              history.push(
                                `/prompt/${res.data.presentation.id}`
                              )
                            })
                        })
                    })
                }
              })
            }}
            className={classes.p365form}>
            <FormInput
              name="month"
              type="number"
              defaultValue={presentation.p365_month || ''}
              required
              min="1"
              max="12"
              placeholder="Month (1 - 12)"
            />
            <FormInput
              name="day"
              type="number"
              defaultValue={presentation.p365_day || ''}
              required
              min="1"
              max="31"
              placeholder="Day (1 - 31)"
            />
            <Button type="Submit" tall>
              Set as Pobble 365
            </Button>
          </form>
        </div>
        <div>
          <FormLabel small>Set differentiated lessons</FormLabel>
          <form
            onSubmit={e => {
              e.preventDefault()
              const easier = e.target.easier.value
              const harder = e.target.harder.value

              let easierReq = null
              let harderReq = null

              if (!easier) {
                easierReq = apiClient.put(
                  `v3/admin/presentations/${id}/unset_easier_version`
                )
              } else {
                easierReq = apiClient
                  .put(`v3/presentations/${easier}/set_to_public`)
                  .then(() => {
                    return apiClient.put(
                      `v3/admin/presentations/${id}/set_easier_version`,
                      {
                        easier_version_id: easier
                      }
                    )
                  })
              }

              if (!harder) {
                harderReq = apiClient.put(
                  `v3/admin/presentations/${id}/unset_harder_version`
                )
              } else {
                harderReq = apiClient
                  .put(`v3/presentations/${harder}/set_to_public`)
                  .then(() => {
                    return apiClient.put(
                      `v3/admin/presentations/${id}/set_harder_version`,
                      {
                        harder_version_id: harder
                      }
                    )
                  })
              }
              Promise.all([easierReq, harderReq])
                .then(() => {
                  toast.success(
                    `Updated sucessfully.\nReload the page to see changes`
                  )
                })
                .catch(() => toast.error('Something went wrong'))
            }}
            className={classes.p365form}>
            <div>
              <FormInput
                defaultValue={presentation.easier_version_id}
                placeholder="Easier lesson ID"
                name="easier"
                type="text"
              />
              {presentation.easier_version_id && (
                <Button
                  _blank
                  href={`/lessons/lesson/${presentation.easier_version_id}`}
                  simple
                  bleed>
                  Open easier
                </Button>
              )}
            </div>
            <div>
              <FormInput
                defaultValue={presentation.harder_version_id}
                placeholder="Harder lesson ID"
                name="harder"
                type="text"
              />
              {presentation.harder_version_id && (
                <Button
                  _blank
                  href={`/lessons/lesson/${presentation.harder_version_id}`}
                  simple
                  bleed>
                  Open harder
                </Button>
              )}
            </div>
            <Button type="Submit" tall>
              Set differentiated lessons
            </Button>
          </form>
        </div>
        <div className={classes.lastRow}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Button href={`/lessons${lessonLink}`} tall secondary>
              Edit
            </Button>
            <Button
              onClick={() => {
                apiClient
                  .post(`/v3/presentations/${id}/create_presentation_copy`)
                  .then(res => {
                    window.open(`/lessons/lesson/${res.data.presentation.id}`)
                  })
              }}
              tall
              secondary>
              Duplicate
            </Button>
          </div>
          <div className={classes.checkboxes}>
            <div className={classes.optionGroup}>
              <FormCheckbox
                checked={currentlyApproved}
                onChange={e => changeApprovedStatus(e.target.checked)}
                id="approve"
              />
              <FormLabel small gapLeft htmlFor="approve">
                Approved
              </FormLabel>
            </div>
            <div className={classes.optionGroup}>
              <FormCheckbox
                checked={currentlyPublic}
                onChange={e => changePublicStatus(e.target.checked)}
                id="public"
              />
              <FormLabel small gapLeft htmlFor="public">
                Public
              </FormLabel>
            </div>
            <div className={classes.optionGroup}>
              <FormCheckbox
                checked={currentlyPremium}
                onChange={e => setPremium(e.target.checked)}
                id="flag"
              />
              <FormLabel small gapLeft htmlFor="flag">
                Premium
              </FormLabel>
            </div>
            <div className={classes.optionGroup}>
              <FormCheckbox
                checked={currentlyFlagged}
                onChange={e => flagPresentation(e.target.checked)}
                id="flag"
              />
              <FormLabel small gapLeft htmlFor="flag">
                Flagged
              </FormLabel>
            </div>
            <div className={classes.optionGroup}>
              <FormCheckbox
                defaultChecked={presentation.sponsored}
                onChange={e => setSponsored(e.target.checked)}
                id="sponsored"
              />
              <FormLabel small gapLeft htmlFor="sponsored">
                Sponsored
              </FormLabel>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default withRouter(injectSheet(styles)(Controls))
